import { Children, type ReactNode } from "react";
import { internalLinkResource, webLinkUrl } from "@mlx/contracts";

/** The workspace link resolvers live in contracts, shared with the server. */
export { internalLinkResource, webLinkUrl };

/** Only bare URLs are compacted. Authored labels and inline code stay intact. */
export function isBareLink(href: string, children: ReactNode): boolean {
  const nodes = Children.toArray(children);
  if (!nodes.length || nodes.some((node) => typeof node !== "string"))
    return false;
  const label = nodes.join("").trim();
  // Markdown encodes a bare link's href ({}, spaces, non-ASCII), so compare it decoded too.
  return (
    label === href ||
    label === safeDecodeURI(href) ||
    (label.startsWith("www.") && href === `http://${label}`)
  );
}

/** A workspace link whose content is not plain words (a linked photo, text
 *  mid-wipe) stays itself rather than becoming a chip, in the link colour. */
export const WORKSPACE_LINK_CONTENT_CLASS = "not-typeset text-link no-underline hover:text-link-hover";

/** An author's link text when it is plain words, which a chip can carry. */
export function plainLabel(children: ReactNode): string | null {
  const nodes = Children.toArray(children);
  if (!nodes.length || nodes.some((node) => typeof node !== "string" && typeof node !== "number")) return null;
  const label = nodes.join("").trim();
  return label || null;
}

function safeDecodeURI(value: string): string {
  try {
    return decodeURI(value);
  } catch {
    return value;
  }
}

export function compactLinkLabel(href: string): string {
  const url = webLinkUrl(href);
  // Keep the real host (including subdomain/port) visible, never URL credentials.
  const host = url?.host ?? "";
  const path = url
    ? `${url.pathname === "/" ? "" : url.pathname}${url.search}${url.hash}`
    : href;
  const available = Math.max(12, 52 - host.length);
  return (
    host + (path.length > available ? `${path.slice(0, available - 1)}…` : path)
  );
}
